import classNames from 'classnames';
import { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '@raydiant/raydial/components/Spinner';

import * as analyticsActions from '../../actions/analytics';
import NavBar from '../Navigation/NavBar';
import Sidebar from '../Sidebar/Sidebar';
import * as paths from '../../routes/paths';
import BannerRouter from '../BannerRouter';
import { useAnalyticsExport } from '../../hooks';
import { getApiQueryParams } from '../../pages/AnalyticsPage/AnalyticsFilter/utils';
import { selectExportType } from '../../selectors/analytics';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { ExportAnalyticsEnum } from '../../types/analytics';
import FreeTrialExpiredDialog from '../FreeTrialExpiredDialog';
import {
  selectIsLiteUser,
  selectUserProfile,
  selectIsLoggingOut,
} from '../../selectors/user';
import { calculateTrialDaysLeft } from '../../pages/LocationsPage/utils';

type AppLayoutProps = {
  backTo?: string;
  children: React.ReactNode;
  hideNavigation?: boolean;
};

const AppLayout = ({
  hideNavigation = false,
  backTo,
  children,
}: AppLayoutProps) => {
  const [isOpen, setIsOpen] = useState(
    () => localStorage.getItem('isSidebarOpen') === 'true',
  );
  const [isFreeTrialExpiredDialogOpen, setIsFreeTrialExpiredDialogOpen] =
    useState(false);
  const { search } = useLocation();
  const query = getApiQueryParams(search);
  const { t } = useTranslation();
  const state = useToastState();
  const dispatch = useDispatch();

  const exportType = useSelector(selectExportType);
  const currentUser = useSelector(selectUserProfile);
  const isLiteUser = useSelector(selectIsLiteUser);
  const isLoggingOut = useSelector(selectIsLoggingOut);

  const daysLeft = calculateTrialDaysLeft(currentUser?.createdAt || '');
  const isPaymentMethodAdded = currentUser?.isStripePaymentMethodAdded;

  const {
    data: exportedData,
    isError: isExportError,
    isLoading: isExportLoading,
  } = useAnalyticsExport(query, exportType);
  const history = useHistory();

  useEffect(() => {
    if (localStorage.getItem('isSidebarOpen') === null) {
      localStorage.setItem('isSidebarOpen', `true`);
      setIsOpen(true);
      window.dispatchEvent(new Event('isSidebarOpenEvent')); // it's needed to follow localStorage.getItem('isSidebarOpen') on the ContetnDetailPage
    }
  }, []);

  useEffect(() => {
    if (isLiteUser && !isPaymentMethodAdded && daysLeft === 0) {
      setIsFreeTrialExpiredDialogOpen(true);
    }
  }, [isLiteUser, isPaymentMethodAdded, daysLeft]);

  const handleToggleSidebar = (value: boolean) => {
    localStorage.setItem('isSidebarOpen', `${value}`);
    setIsOpen(value);
    window.dispatchEvent(new Event('isSidebarOpenEvent')); // it's needed to follow localStorage.getItem('isSidebarOpen') on the ContetnDetailPage
  };

  const devicesPage = !!useRouteMatch({ path: paths.screens.pattern });
  const libraryPage = !!useRouteMatch({ path: paths.library.pattern });
  const marketplacePage = !!useRouteMatch({ path: paths.marketplace.pattern });
  const accountPage = !!useRouteMatch({ path: paths.account.pattern });
  const analyticsPage = !!useRouteMatch({ path: paths.analytics.pattern });
  const aiAnalyticsPage = !!useRouteMatch({ path: paths.aiAnalytics.pattern });
  const contentEngagementPage = !!useRouteMatch({
    path: paths.contentEngagement.pattern,
  });
  const catalogs = !!useRouteMatch({
    path: paths.catalogs.pattern,
  });
  const gettingStartedPage = !!useRouteMatch({
    path: paths.gettingStarted.pattern,
  });
  const plansPage = !!useRouteMatch({ path: paths.plans.pattern });

  const navigationPages =
    devicesPage ||
    libraryPage ||
    marketplacePage ||
    accountPage ||
    analyticsPage ||
    aiAnalyticsPage ||
    contentEngagementPage ||
    catalogs ||
    gettingStartedPage ||
    plansPage;

  const handleDownload = useCallback(
    (exportedData: BlobPart, filename: string, mimeType: string) => {
      const blob = new Blob([exportedData], { type: mimeType });
      saveAs(blob, filename);
    },
    [],
  );

  const handleSubscribe = () => {
    history.push(paths.account());
    setIsFreeTrialExpiredDialogOpen(false);
  };

  useEffect(() => {
    if (isExportLoading || !exportType) {
      return;
    }

    if (isExportError) {
      console.error('Export error');
      state.add({
        title: t('export.exportingToasts.errorTitle'),
        description: t('export.exportingToasts.errorDescription'),
        variant: 'caution',
      });
      return;
    }

    if (exportedData) {
      let filename = `export_${exportType}.csv`;
      let mimeType = 'text/csv;charset=utf-8';

      if (exportType === ExportAnalyticsEnum.ContentEngagement) {
        filename = `export_${exportType}.zip`;
        mimeType = 'application/zip';
      }

      handleDownload(exportedData, filename, mimeType);
    }

    dispatch(analyticsActions.setExportType(''));
  }, [
    isExportLoading,
    exportType,
    exportedData,
    isExportError,
    dispatch,
    state,
    t,
    handleDownload,
  ]);

  if (hideNavigation) {
    return <>{children}</>;
  }

  if (!currentUser) {
    return (
      <div className={classNames('app-container')}>
        <div className="main">
          <NavBar />
          <main className="main-wrapper flex flex-col w-full justify-center items-center">
            <div className="flex flex-col justify-center items-center gap-4">
              <Spinner className="size-14 text-primary" />
              <span className="text-xl font-semibold">
                {isLoggingOut
                  ? 'Signing out...'
                  : 'Getting ready to displai brilliance'}
              </span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('app-container')}>
      <div className={isOpen ? 'overlay' : ''}></div>
      <div className="main">
        <NavBar onToggleMenu={() => handleToggleSidebar(!isOpen)} />
        <FreeTrialExpiredDialog
          isOpen={isFreeTrialExpiredDialogOpen}
          onSubscribe={handleSubscribe}
          onClose={() => setIsFreeTrialExpiredDialogOpen(false)}
        />
        <section className="wrapper">
          <Sidebar
            onClose={() => handleToggleSidebar(false)}
            isOpen={isOpen}
            onTopMenuClick={handleToggleSidebar}
          />
          <main className="flex flex-col w-full overflow-auto main-wrapper">
            {navigationPages && <BannerRouter />}
            {children}
          </main>
        </section>
      </div>
    </div>
  );
};

export default AppLayout;
